/**
 * The Tasks calendar's MONTH view: its data seam. The view itself is
 * `calendar/MonthView.tsx` (rebuilt round the plan check on 2026-10-31).
 *
 * Like `CalendarLanesContainer` for Day, Week and Agenda: the minute
 * clock and `useCalendarLaneData` over the month (the same live tasks,
 * finished tasks, schedule runs, fire events and robots the lanes draw),
 * plus the plan check for the month's days from today on -- asked for
 * only for the roles it is for (`usePlanCheck`), and only while some of
 * the month is still to come. Which task types the grid hides is
 * remembered per browser (`calendar-prefs.ts`).
 *
 * Until 2026-10-31 this file drew the month itself, on the BROWSER's
 * clock, with up to three task blocks per cell, a "did not run" list over
 * the grid and the site's schedule list in the sidebar. The grid is on the
 * site's calendar now, like the lanes; a day's work that didn't finish is
 * listed in that day's panel; and the schedules are the Agenda's.
 */
import { useCallback, useMemo, useState } from 'react';
import type { TaskState } from '../../api/rmf-client';
import type { MaintenanceWindow, ScheduleOccurrence, TaskSchedule } from '../../api/v1';
import {
  PLAN_CHECK_PREVIEW_NOTE,
  planCheckErrorText,
  planCheckRetryable,
  usePlanCheck,
  usePlanCheckAccess,
} from '../../api/hooks/usePlanCheck';
import type { LanePaint } from './calendar-categories';
import type { LanesNavigation } from './calendar/CalendarLanes';
import { loadMonthHidden, saveMonthHidden } from './calendar/calendar-prefs';
import { dateKeyOf, type DateKey } from './calendar/lane-time';
import { monthPlanRange, monthWindow, newTaskStart } from './calendar/month-model';
import { MonthView } from './calendar/MonthView';
import { useCalendarLaneData } from './calendar/useCalendarLaneData';
import { useMinuteNow } from './calendar/useMinuteNow';

export type CalendarView = 'day' | 'week' | 'month' | 'agenda';

/** A start and end the new-task form opens on. */
export interface CalendarSlot {
  start: Date;
  end: Date;
}

export interface TaskCalendarProps {
  siteId: string;
  /** Any day of the month to show, on the site's calendar. */
  dateKey: DateKey;
  timeZone: string;
  tasks: readonly TaskState[];
  /** The plan for the month (`monthWindow`), read by the screen. */
  occurrences: readonly ScheduleOccurrence[];
  schedules: readonly TaskSchedule[];
  maintenance: readonly MaintenanceWindow[];
  /** The month's blocked times could not be read: no day can be badged
   *  "Blocked", and the runs they hold are counted as planned. */
  maintenanceFailed?: boolean;
  onNavigate: (patch: LanesNavigation) => void;
  /** The toolbar's "+ New task". Withheld for a role that may not. */
  onNewTask?: () => void;
  /** "New task on this day": the new-task form, its start filled in as a
   *  browser `Date` reading the site's wall clock (`newTaskStart`).
   *  Withheld for a role that may not create tasks. */
  onNewTaskOnDay?: (start: Date) => void;
}

export function TaskCalendar(props: TaskCalendarProps) {
  const { siteId, dateKey, timeZone } = props;
  const nowMs = useMinuteNow();
  const todayKey = dateKeyOf(nowMs, timeZone);
  const window = useMemo(() => monthWindow(dateKey, timeZone), [dateKey, timeZone]);
  const data = useCalendarLaneData({
    siteId,
    window,
    nowMs,
    tasks: props.tasks,
    occurrences: props.occurrences,
    schedules: props.schedules,
    maintenance: props.maintenance,
  });

  const planAccess = usePlanCheckAccess();
  const range = useMemo(() => monthPlanRange(dateKey, todayKey), [dateKey, todayKey]);
  const plan = usePlanCheck(range);

  const [hidden, setHidden] = useState<Set<LanePaint>>(() => loadMonthHidden(siteId));
  const toggleType = useCallback((paint: LanePaint) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(paint)) next.delete(paint);
      else next.add(paint);
      saveMonthHidden(next);
      return next;
    });
  }, []);

  const onNewTaskOnDay = props.onNewTaskOnDay;
  return (
    <MonthView
      dateKey={dateKey}
      todayKey={todayKey}
      timeZone={timeZone}
      nowMs={nowMs}
      data={data}
      blockedTimesFailed={props.maintenanceFailed}
      plan={{
        shown: planAccess.shown,
        data: plan.data,
        loading: plan.isLoading,
        error: plan.isError && !plan.data ? planCheckErrorText(plan.error) : null,
        onRetry:
          plan.isError && planCheckRetryable(plan.error) ? () => void plan.refetch() : undefined,
        note: planAccess.shown && !planAccess.readable ? PLAN_CHECK_PREVIEW_NOTE : null,
      }}
      hidden={hidden}
      onToggleType={toggleType}
      onNavigate={props.onNavigate}
      onNewTask={props.onNewTask}
      onNewTaskOnDay={
        onNewTaskOnDay
          ? (key) => onNewTaskOnDay(newTaskStart(key, todayKey, Date.now(), timeZone))
          : undefined
      }
    />
  );
}
